import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  CalendarCheck,
  CheckCircle2,
  Clock3,
  CreditCard,
  FolderKanban,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  UserRound,
  Video,
} from "lucide-react";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger.js";
import "./DetailPages.css";
import {
  bookConsultation,
  formatConsultationCurrency,
  formatConsultationDateTime,
  getConsultationDescription,
  getConsultationDurationLabel,
  getConsultationSummary,
  getConsultationTitle,
  getConsultantName,
  getMyConsultations,
  getConsultationDetails,
  listConsultationReviews,
} from "../api/consultations";

const consultationFallbackImage =
  "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1400&q=80";

function getDefaultTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function toApiDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function getStatusMeta(status, isAr) {
  const map = {
    confirmed: { className: "text-bg-success", label: isAr ? "مؤكدة" : "Confirmed" },
    pending: { className: "text-bg-warning", label: isAr ? "قيد المراجعة" : "Pending" },
    completed: { className: "text-bg-primary", label: isAr ? "مكتملة" : "Completed" },
    cancelled: { className: "text-bg-danger", label: isAr ? "ملغية" : "Cancelled" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

export default function ConsultationDetails() {
  const { consultationRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const isAr = lang === "ar";

  const [consultation, setConsultation] = useState(null);
  const [existingBooking, setExistingBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({
    scheduled_at: "",
    timezone: getDefaultTimezone(),
    user_notes: "",
  });

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setNotice("");

        const [details, consultationReviews] = await Promise.all([
          getConsultationDetails(consultationRef),
          listConsultationReviews(consultationRef).catch(() => []),
        ]);
        if (!active) return;

        setConsultation(details);
        setReviews(Array.isArray(consultationReviews) ? consultationReviews : []);

        if (isLogged) {
          try {
            const bookings = await getMyConsultations();
            if (!active) return;
            const matched = bookings.find((item) => Number(item?.consultation?.id) === Number(details?.id));
            setExistingBooking(matched || null);
          } catch (bookingsError) {
            logger.warn("My consultations fetch failed", bookingsError);
          }
        } else {
          setExistingBooking(null);
        }
      } catch (err) {
        logger.error("Consultation details error", err);
        if (!active) return;
        setError(isAr ? "تعذر تحميل تفاصيل الاستشارة." : "Failed to load consultation details.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [consultationRef, isAr, isLogged]);

  const title = useMemo(() => getConsultationTitle(consultation, isAr), [consultation, isAr]);
  const summary = useMemo(() => getConsultationSummary(consultation, isAr), [consultation, isAr]);
  const description = useMemo(() => getConsultationDescription(consultation, isAr), [consultation, isAr]);
  const consultationUrlRef = consultation?.slug || consultation?.id || consultationRef;
  const isPaid = Number(consultation?.price || 0) > 0;
  const statusMeta = getStatusMeta(existingBooking?.status, isAr);
  const reviewCount = Number(consultation?.reviews_count || reviews.length || 0);
  const coverImage = consultation?.cover_image || consultationFallbackImage;

  const facts = [
    {
      key: "provider",
      icon: UserRound,
      label: isAr ? "المستشار" : "Consultant",
      value: getConsultantName(consultation, isAr),
    },
    {
      key: "duration",
      icon: Clock3,
      label: isAr ? "المدة" : "Duration",
      value: getConsultationDurationLabel(consultation, isAr),
    },
    {
      key: "meeting",
      icon: Video,
      label: isAr ? "طريقة الاجتماع" : "Meeting",
      value: consultation?.meeting_provider || (isAr ? "أونلاين" : "Online"),
    },
    {
      key: "bookings",
      icon: CalendarCheck,
      label: isAr ? "الحجوزات" : "Bookings",
      value: String(consultation?.bookings_count || 0),
    },
    {
      key: "reviews",
      icon: MessageSquareText,
      label: isAr ? "التقييمات" : "Reviews",
      value: String(reviewCount),
    },
  ];

  const handleRequireAuth = () => {
    navigate("/login", { state: { from: location } });
  };

  const handleFormChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleBook = async () => {
    if (!consultation) return;
    if (!isLogged) {
      handleRequireAuth();
      return;
    }

    if (isPaid) {
      navigate(`/consulting/${consultationUrlRef}/checkout`);
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      setNotice("");

      const payload = {
        timezone: form.timezone || getDefaultTimezone(),
        user_notes: form.user_notes.trim(),
      };

      const scheduledAt = toApiDateTime(form.scheduled_at);
      if (scheduledAt) payload.scheduled_at = scheduledAt;

      const response = await bookConsultation(consultation.id, payload);
      const booking = response?.data?.booking || null;
      setExistingBooking(booking);
      setNotice(response?.message || (isAr ? "تم حجز الاستشارة بنجاح." : "Consultation booked successfully."));
    } catch (err) {
      logger.error("Consultation booking error", err);
      setError(err?.message || (isAr ? "تعذر إتمام الحجز الآن." : "Unable to complete the booking right now."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;
    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = consultationFallbackImage;
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p className="mb-0">{isAr ? "جاري تحميل الاستشارة..." : "Loading consultation..."}</p>
      </div>
    );
  }

  if (error || !consultation?.id) {
    return (
      <div className="detail-pages-container course-detail-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="course-detail-shell">
          <div className="alert alert-danger rounded-4">
            {error || (isAr ? "تعذر تحميل تفاصيل الاستشارة." : "Failed to load consultation details.")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-pages-container course-detail-page consultation-detail-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="course-detail-shell">
        <div className="course-detail-backbar">
          <Link to="/consulting" className="course-detail-backlink">
            <ArrowLeft size={16} />
            {isAr ? "الرجوع إلى الاستشارات" : "Back to Consultations"}
          </Link>
        </div>

        <section className="course-detail-hero-v2">
          <div className="course-detail-hero-v2__main">
            <div className="detail-image-wrapper course-detail-image-wrap course-detail-hero-v2__media">
              <img src={coverImage} alt={title} className="detail-image course-detail-image" onError={handleImageError} />
            </div>

            <div className="course-detail-hero-v2__copy">
              <div className="course-detail-topline">
                <span className="course-detail-pill">
                  <FolderKanban size={14} />
                  {consultation?.category || (isAr ? "استشارة متخصصة" : "Specialized Consultation")}
                </span>
                <span className="course-detail-pill course-detail-pill--soft">
                  <Clock3 size={14} />
                  {getConsultationDurationLabel(consultation, isAr)}
                </span>
              </div>

              <h1 className="detail-info-title course-detail-hero-v2__title">{title}</h1>

              <div className="course-detail-hero-v2__meta">
                <div className="detail-rating">
                  <StarRating value={consultation?.average_rating || 0} size={18} showValue />
                  <span className="detail-reviews">({reviewCount} {isAr ? "تقييم" : "reviews"})</span>
                </div>
                <div className="course-detail-hero-v2__instructor">
                  <UserRound size={16} />
                  <span>{getConsultantName(consultation, isAr)}</span>
                </div>
              </div>

              {summary ? <p className="detail-desc course-detail-hero-v2__summary">{summary}</p> : null}

              <div className="course-detail-quick-stats">
                {facts.slice(1, 5).map((fact) => {
                  const Icon = fact.icon;
                  return (
                    <div key={fact.key} className="course-detail-quick-stat">
                      <span className="course-detail-quick-stat__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{fact.label}</small>
                        <strong>{fact.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <aside className="course-detail-purchase-card">
            <div className="course-detail-purchase-card__eyebrow">
              <Sparkles size={14} />
              {isAr ? "حجز مرن" : "Flexible booking"}
            </div>

            <div className="detail-price course-detail-purchase-card__price">
              <span className="detail-current">
                {isPaid ? formatConsultationCurrency(consultation?.price, consultation?.currency) : isAr ? "مجانية" : "Free"}
              </span>
            </div>

            {notice ? <div className="alert alert-success rounded-3 mb-3">{notice}</div> : null}
            {error ? <div className="alert alert-danger rounded-3 mb-3">{error}</div> : null}

            {existingBooking ? (
              <div className="detail-modern-form">
                <div className="detail-modern-notice">
                  <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                  <strong>{isAr ? "لديك حجز لهذه الاستشارة" : "You have an existing booking"}</strong>
                </div>
                <Link to="/my-consultations" className="btn btn-primary btn-lg course-detail-purchase-card__btn">
                  {isAr ? "استشاراتي" : "My Consultations"}
                </Link>
              </div>
            ) : (
              <form className="detail-modern-form" onSubmit={(event) => { event.preventDefault(); handleBook(); }}>
                <label>
                  <span>{isAr ? "الوقت المفضل" : "Preferred time"}</span>
                  <input type="datetime-local" value={form.scheduled_at} onChange={handleFormChange("scheduled_at")} />
                </label>
                <label>
                  <span>{isAr ? "ملاحظاتك" : "Your notes"}</span>
                  <textarea
                    rows="3"
                    value={form.user_notes}
                    onChange={handleFormChange("user_notes")}
                    placeholder={isAr ? "أي ملاحظات أو أسئلة" : "Any notes or questions"}
                  />
                </label>
                <button type="submit" className="btn btn-primary btn-lg course-detail-purchase-card__btn" disabled={actionLoading}>
                  {actionLoading
                    ? isAr ? "جاري الحجز..." : "Booking..."
                    : isPaid ? (isAr ? "متابعة للدفع" : "Proceed to Payment") : (isAr ? "احجز الآن" : "Book Now")}
                </button>
              </form>
            )}

            <div className="course-detail-purchase-card__trust">
              <div>
                <ShieldCheck size={16} />
                <span>{isAr ? "تفاصيل واضحة قبل تأكيد الحجز" : "Clear details before confirming"}</span>
              </div>
              <div>
                <CreditCard size={16} />
                <span>{isAr ? "الدفع يظهر بشكل منفصل للجلسات المدفوعة" : "Checkout is shown separately for paid sessions"}</span>
              </div>
              <div>
                <CheckCircle2 size={16} />
                <span>{isAr ? "متابعة الحجز من صفحة استشاراتي" : "Track bookings from My Consultations"}</span>
              </div>
            </div>
          </aside>
        </section>

        <section className="course-detail-layout-v2">
          <main className="course-detail-main-v2">
            <article className="course-detail-section-card">
              <div className="course-detail-section-card__head">
                <h3 className="detail-info-subtitle course-detail-section-card__title">
                  {isAr ? "عن الاستشارة" : "About this consultation"}
                </h3>
              </div>
              <div
                className="detail-rich-text"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description || (isAr ? "<p>لا يوجد وصف مفصل بعد.</p>" : "<p>No detailed description yet.</p>")),
                }}
              />
            </article>

            <article className="course-detail-section-card">
              <div className="course-detail-section-card__head">
                <h3 className="detail-info-subtitle course-detail-section-card__title">
                  {isAr ? "كيف يعمل الحجز" : "How booking works"}
                </h3>
              </div>
              <div className="course-detail-side-notes">
                <div>
                  <CheckCircle2 size={16} />
                  <span>{isAr ? "اختر وقتًا مناسبًا أو اتركه فارغًا للتنسيق لاحقًا." : "Pick a preferred time or leave it open for later coordination."}</span>
                </div>
                <div>
                  <CheckCircle2 size={16} />
                  <span>{isAr ? "الجلسات المجانية تؤكد مباشرة، والمدفوعة تنتقل إلى الدفع." : "Free sessions confirm instantly, while paid sessions go to checkout."}</span>
                </div>
                <div>
                  <CheckCircle2 size={16} />
                  <span>{isAr ? "تابع حالة الحجز والرابط من صفحة استشاراتي." : "Track status and meeting links from My Consultations."}</span>
                </div>
              </div>
            </article>

            {reviews.length > 0 && (
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h3 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "التقييمات" : "Reviews"}
                  </h3>
                </div>
                <div className="detail-reviews-list course-detail-reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="detail-review-item course-detail-review-card">
                      <div className="d-flex justify-content-between mb-2 align-items-start gap-2">
                        <strong>{review.user?.name || (isAr ? "عميل" : "Client")}</strong>
                        <StarRating value={review.rating || 0} size={14} showValue />
                      </div>
                      <p className="text-muted small mb-0">{review.comment || (isAr ? "بدون تعليق إضافي." : "No additional comment.")}</p>
                    </div>
                  ))}
                </div>
              </article>
            )}
          </main>

          <aside className="course-detail-side-v2">
            <article className="course-detail-side-card">
              <h4>{isAr ? "معلومات الاستشارة" : "Consultation Facts"}</h4>
              <div className="course-detail-facts">
                {facts.map((fact) => {
                  const Icon = fact.icon;
                  return (
                    <div key={fact.key} className="course-detail-fact">
                      <span className="course-detail-fact__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{fact.label}</small>
                        <strong>{fact.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            {existingBooking ? (
              <article className="course-detail-side-card course-detail-side-card--accent">
                <h4>{isAr ? "تفاصيل حجزك" : "Your Booking"}</h4>
                <div className="course-detail-facts">
                  <div className="course-detail-fact">
                    <span className="course-detail-fact__icon"><CalendarCheck size={16} /></span>
                    <div>
                      <small>{isAr ? "رقم الحجز" : "Booking ID"}</small>
                      <strong>#{existingBooking.id}</strong>
                    </div>
                  </div>
                  <div className="course-detail-fact">
                    <span className="course-detail-fact__icon"><CheckCircle2 size={16} /></span>
                    <div>
                      <small>{isAr ? "الحالة" : "Status"}</small>
                      <strong>{statusMeta.label}</strong>
                    </div>
                  </div>
                  {existingBooking.scheduled_at ? (
                    <div className="course-detail-fact">
                      <span className="course-detail-fact__icon"><Clock3 size={16} /></span>
                      <div>
                        <small>{isAr ? "الموعد" : "Scheduled"}</small>
                        <strong>{formatConsultationDateTime(existingBooking.scheduled_at, isAr ? "ar" : "en")}</strong>
                      </div>
                    </div>
                  ) : null}
                </div>
              </article>
            ) : null}
          </aside>
        </section>
      </div>
    </div>
  );
}
